import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, isPlatformBrowser } from '@angular/common';
import { Component, inject, LOCALE_ID, OnInit, PLATFORM_ID } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  SUPPORTED_LOCALE,
  SUPPORTED_LOCALES,
} from 'src/app/services/locale.service';
import { LocalStorageService } from 'src/app/services/privacy/storage/local-storage.service';

@Component({
  selector: 'app-locale-selector',
  templateUrl: './locale-selector.component.html',
  styleUrls: ['./locale-selector.component.scss'],
  imports: [MatButtonModule, MatIconModule, MatMenuModule, AsyncPipe],
})
export class LocaleSelectorComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  readonly SUPPORTED_LOCALES = SUPPORTED_LOCALES;
  selectedLocale: SUPPORTED_LOCALE;
  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay()
  );

  constructor() {
    const localeId = inject(LOCALE_ID);
    if (isSupportedLocale(localeId)) {
      this.selectedLocale = localeId as SUPPORTED_LOCALE;
    } else {
      this.selectedLocale = DEFAULT_LOCALE;
    }
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.isHandset$ = new Observable<boolean>((subscriber) => {
        subscriber.next(false);
        subscriber.complete();
      });
    }
  }

  getFlagClass(localeId: SUPPORTED_LOCALE): string {
    const locale = new Intl.Locale(localeId);
    const region = locale.region?.toLowerCase();
    return region ? `fi fi-${region}` : 'fi';
  }

  getLocaleLabel(localeId: SUPPORTED_LOCALE): string {
    const locale = new Intl.Locale(localeId);
    const region = locale.region;
    const language = locale.language;
    const regionNames = new Intl.DisplayNames([localeId], { type: 'region' });
    const countryName = region ? regionNames.of(region) : undefined;

    if (!countryName || !language) {
      return localeId;
    }

    return countryName + ' (' + language.toUpperCase() + ')';
  }

  setLocale(localeId: SUPPORTED_LOCALE, event: Event) {
    event.preventDefault(); // Prevent default navigation to href
    this.localStorageService.LocaleId = localeId;
    if (isPlatformBrowser(this.platformId)) {
      window.location.href = '/' + localeId;
    }
  }
}
